/**
 * GOLD DIGGERS PERFORMANCE POOL DASHBOARD — v4.3.76
 *
 * Single intelligent page that adapts to the user's state:
 * 1. No team + not qualified → Shows qualification tracker with progress bars + how it works
 * 2. No team + qualified → Shows congratulations + team registration form
 * 3. Has team → Shows team card + live leaderboard + pool stats
 *
 * The page always shows the leaderboard at the bottom regardless of state.
 *
 * v4.3.76: Tier ladder (Prospector -> Tycoon) retired. Qualification is a
 *          single gate: 5 L1 / 5 L2 referrals each holding >= 1 share.
 *          All winners receive a flat 80% USDT / 20% Shares payout split.
 *          Legacy users whose highest_achieved_category is 'miner'/
 *          'excavator'/'operator'/'tycoon' remain qualified (treated as
 *          any non-null category) and receive the same flat split.
 * v4.3.73: Relabelled "Score" → "Team Volume (USD)" for the volume
 *          scoring model (unweighted total USD across the downline).
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePortfolioData } from '../hooks/usePortfolioData';
import { ensureSessionHeaders } from '../lib/serverSession';
import { API_BASE_URL } from '../constants';
import { POOL_CONTRIBUTION_PERCENT, POOL_WINNER_COUNT, payoutDistributionForDate } from '../lib/goldDiggersConstants.js';
import { memberShareTotals, totalContributionShares } from '../lib/goldDiggersMemberShares.js';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TeamData { id: number; name: string; category: string; created_at: string; }
interface Contribution {
  user: string;
  level: number;
  shares: number;
  weighted: number;
  time: string;
  sponsor?: string | null;
  sponsor_pool_rank?: number | null;
  sponsor_team_name?: string | null;
  sponsor_eligible?: boolean;
}
interface LeaderboardEntry {
  team_id: number; team_name: string; leader_username: string; category: string;
  // v4.3.72: weighted_score is a back-compat alias; numerically identical to
  // total_volume_usd (USD, unweighted). l1_shares is deprecated (always 0).
  weighted_score: number; raw_shares: number; l1_shares: number;
  total_volume_usd?: number; total_shares?: number;
  eligible_for_payout?: boolean;
  sponsor?: string | null;
  sponsor_pool_rank?: number | null;
  sponsor_team_name?: string | null;
  pool_pct?: number | null;
  prize_amount?: number;
  bonus_amount?: number;
  payout_total?: number;
  contributions?: Contribution[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

// v4.3.76: Single qualification gate replaces the 5-tier ladder.
// A user is 'Qualified' once they have 5 L1 and 5 L2 referrals each
// holding at least 1 share. All winners receive the same 80% USDT /
// 20% Shares split regardless of any legacy stored category.
const CATEGORIES = [
  { name: 'Qualified', l1: 5, l2: 5, shares: 1, color: '#FFD700', icon: '⛏️', desc: 'Meet the gate to compete in the daily pool', usdtPct: 80, sharePct: 20 },
];

const __LEVEL_WEIGHTS = [1.0, 0.5, 0.25, 0.125, 0.0625, 0.03125, 0.015625, 0.0078125, 0.00390625, 0.001953125];
const SPONSOR_COLOR = '#F5C542';
const DOWNLINE_COLOR = '#5EC8F8';
const formatUtcStamp = (ts?: string | null) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};
const lineageHue = (key?: string | null) => {
  const s = (key || '').trim().toLowerCase();
  if (!s) return 220;
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h) % 360;
};
const lineageStripe = (key?: string | null) => `hsl(${lineageHue(key)} 72% 48%)`;

// Temporarily disables all pool-earning claim UI (Claim All + per-row Claim).
// Flip to `true` to restore claims for all team leaders.
const CLAIMS_ENABLED = false;

const TEAM_NAMES = [
  'Aureus Titans', 'Golden Vanguard', 'Diamond Strikers', 'Gold Rush Legion',
  'Bullion Brigade', 'Midas Force', 'Nugget Nation', 'Fort Knox Crew',
  'Gold Standard Elite', 'Crown Syndicate', 'Sovereign Miners', 'El Dorado Squad',
  'Goldfield Rangers', 'Auric Warriors', 'Treasure Hunters United',
  'Golden Eagles', 'Pinnacle Prospectors', 'Apex Gold Mining', 'Royal Excavators',
  'Iron Gold Alliance', 'Sterling Strike Force', 'Deep Shaft Legends',
];

function DualHScroll({ children, deps }: { children: React.ReactNode; deps: unknown[] }) {
  const topRef = useRef<HTMLDivElement>(null);
  const botRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const lock = useRef(false);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.scrollWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, deps);

  const sync = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (!from || !to || lock.current) return;
    lock.current = true;
    to.scrollLeft = from.scrollLeft;
    requestAnimationFrame(() => {
      lock.current = false;
    });
  };

  return (
    <div>
      <div
        ref={topRef}
        className="overflow-x-auto overflow-y-hidden sticky top-0 z-20"
        style={{ background: '#111827' }}
        onScroll={() => sync(topRef.current, botRef.current)}
      >
        <div style={{ width: Math.max(width, 1), height: 12 }} />
      </div>
      <div
        ref={botRef}
        className="overflow-x-auto"
        onScroll={() => sync(botRef.current, topRef.current)}
      >
        <div ref={innerRef} className="min-w-max">
          {children}
        </div>
      </div>
    </div>
  );
}

interface Props { user: any; onBack?: () => void; }

export const GoldDiggersDashboard: React.FC<Props> = ({ user, onBack }) => {
  const { t } = useTranslation('common');
  const userId = user?.database_user?.id || user?.id;
  const { summary } = usePortfolioData(userId);

  // ─── State ──────────────────────────────────────────────────────────────
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [poolTotal, setPoolTotal] = useState(0);
  const [remainderTotal, setRemainderTotal] = useState(0);
  const [volumeTeamCount, setVolumeTeamCount] = useState(0);
  const [competitionDate, setCompetitionDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [lbHasMore, setLbHasMore] = useState(false);
  const [lbTotalTeams, setLbTotalTeams] = useState(0);
  const [lbLoadingMore, setLbLoadingMore] = useState(false);
  // v4.7.24: track current leaderboard length via ref so loadLeaderboard's
  // useCallback identity stays stable across appends. Previously the callback
  // depended on leaderboard.length, which caused the mount-effect to re-fire
  // a fresh append=false fetch right after every "Load More Teams" click,
  // collapsing the just-loaded extra rows back to the first 10.
  const leaderboardLengthRef = useRef(0);

  // Pool history
  const [poolHistory, setPoolHistory] = useState<{ date: string; pool_total: number; winners: { rank: number; team_name: string; category: string; amount_awarded: number; claimed: boolean }[] }[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const __HISTORY_PER_PAGE = 1;
  const [myTeam, setMyTeam] = useState<TeamData | null>(null);
  const [l1Count, setL1Count] = useState(0);
  const [l2Count, setL2Count] = useState(0);
  const [qualifiedCategory, setQualifiedCategory] = useState<string | null>(null);
  const [qualLoading, setQualLoading] = useState(true);

  // Registration
  const [teamName, setTeamName] = useState('');
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  const [nameChecking, setNameChecking] = useState(false);
  const [nameSuggestion, setNameSuggestion] = useState('');
  const [registering, setRegistering] = useState(false);
  const [regError, setRegError] = useState('');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // UI
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [expandedTier, setExpandedTier] = useState<string | null>(null);
  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null);
  const [unregPage, setUnregPage] = useState(0);
  const UNREG_PER_PAGE = 15;
  const [pastPools, setPastPools] = useState<{ day: string; total: number; shares_sold: number; revenue: number }[]>([]);
  const [unregistered, setUnregistered] = useState<{ masked_username: string; category: string; l1_count: number; l2_count: number }[]>([]);
  const [tierQualified, setTierQualified] = useState<Record<number, { l1: number; l2: number }>>({});
  const [earnings, setEarnings] = useState<{ total: number; unclaimed_total?: number; history: { id: number; date: string; rank: number; amount: number; usdt_portion: number; share_portion: number; pool: number; score: number; paid: boolean; claimed: boolean; source: string }[] }>({ total: 0, history: [] });
  const [claiming, setClaiming] = useState(false);
  const [claimingId, setClaimingId] = useState<number | null>(null); // Track which specific record is being claimed
  const [claimResult, setClaimResult] = useState<string | null>(null);

  const currentUsername = user?.database_user?.username || user?.username;

  // v4.3.76: Single-gate qualification. A user is qualified when the
  // backend reports a stored highest_achieved_category (any of the legacy
  // tiers count as qualified) OR when the live tierQualified counts meet
  // the 5 L1 / 5 L2 >= 1 share gate. Legacy stored values like 'tycoon'
  // remain valid qualifications; they just resolve to the single flat
  // payout split and display as 'Qualified'.
  const derivedCategory = useMemo(() => {
    if (qualifiedCategory) return null; // already have a DB-stored category
    if (!tierQualified || Object.keys(tierQualified).length === 0) return null;
    const gate = CATEGORIES[0];
    const tq = tierQualified[gate.shares];
    if (tq && tq.l1 >= gate.l1 && tq.l2 >= gate.l2) return gate.name.toLowerCase();
    return null;
  }, [qualifiedCategory, tierQualified]);

  const userCategory = qualifiedCategory || derivedCategory || myTeam?.category || null;
  // Any non-null stored category is treated as qualified (covers legacy
  // 'prospector'/'miner'/'excavator'/'operator'/'tycoon' values). Display
  // uses the sole CATEGORIES[0] entry regardless of legacy value.
  const isQualified = !!userCategory;
  const catIdx = isQualified ? 0 : -1;
  const hasTeam = !!myTeam;

  // ─── Data Loading ──────────────────────────────────────────────────────

  const loadLeaderboard = useCallback(async (append = false) => {
    try {
      if (append) setLbLoadingMore(true); else setLoading(true);
      const h = await ensureSessionHeaders();
      // v4.7.24: read length from ref so this callback is dep-free and stable
      const currentOffset = append ? leaderboardLengthRef.current : 0;
      const r = await fetch(`${API_BASE_URL}/api/teams/leaderboard?limit=${POOL_WINNER_COUNT}&offset=${currentOffset}`, { headers: h });
      if (r.ok) {
        const d = await r.json();
        if (append) {
          setLeaderboard(prev => {
            const next = [...prev, ...(d.leaderboard || [])];
            leaderboardLengthRef.current = next.length;
            return next;
          });
        } else {
          const next = d.leaderboard || [];
          leaderboardLengthRef.current = next.length;
          setLeaderboard(next);
        }
        setPoolTotal(d.poolTotal || 0);
        setRemainderTotal(d.remainderTotal || 0);
        setVolumeTeamCount(d.volumeTeamCount || 0);
        setCompetitionDate(d.competitionDate || '');
        setLbHasMore(d.hasMore || false);
        setLbTotalTeams(d.totalTeams || 0);
      }
    } catch { /* */ } finally { setLoading(false); setLbLoadingMore(false); }
  }, []);

  const loadQualification = useCallback(async () => {
    try {
      setQualLoading(true);
      const h = await ensureSessionHeaders();

      // Single endpoint returns L1 count, L2 count, rank, and team — all via SQL counts
      const r = await fetch(`${API_BASE_URL}/api/teams/my-status`, { headers: h });
      if (r.ok) {
        const d = await r.json();
        const s = d?.data;
        if (s) {
          setL1Count(s.l1_count || 0);
          setL2Count(s.l2_count || 0);
          if (s.highest_achieved_category) setQualifiedCategory(s.highest_achieved_category);
          if (s.team) {
            setMyTeam(s.team);
            // If auto-provisioned, open rename mode so user can personalize
            if (s.auto_provisioned) {
              setRenameMode(true);
              setNewTeamName(s.team.name);
            }
          }
          if (s.past_pools) setPastPools(s.past_pools);
          if (s.unregistered_qualified) setUnregistered(s.unregistered_qualified);
          if (s.tier_qualified) setTierQualified(s.tier_qualified);
          if (s.earnings) setEarnings(s.earnings);
        }
      }
    } catch (err) {
      console.error('[GoldDiggers] Status load error:', err);
    } finally { setQualLoading(false); }
  }, []);

  // Derive myTeam from leaderboard
  useEffect(() => {
    if (leaderboard.length > 0 && currentUsername) {
      const mine = leaderboard.find(t => t.leader_username === currentUsername);
      if (mine) setMyTeam({ id: mine.team_id, name: mine.team_name, category: mine.category, created_at: '' });
    }
  }, [leaderboard, currentUsername]);

  const loadPoolHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const h = await ensureSessionHeaders();
      const r = await fetch(`${API_BASE_URL}/api/teams/pool-history?days=14`, { headers: h });
      if (r.ok) { const d = await r.json(); setPoolHistory(d.history || []); }
    } catch { /* */ } finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { loadLeaderboard(); loadQualification(); loadPoolHistory(); }, [loadLeaderboard, loadQualification, loadPoolHistory]);
  useEffect(() => { const i = setInterval(() => loadLeaderboard(false), 60000); return () => clearInterval(i); }, [loadLeaderboard]);

  // ─── Name Validation (debounced) ───────────────────────────────────────
  useEffect(() => { return () => { if (debounceRef.current) clearTimeout(debounceRef.current); }; }, []);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!teamName || teamName.trim().length < 3) { setNameAvailable(null); setNameSuggestion(''); return; }
    let cancelled = false;
    setNameChecking(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const h = await ensureSessionHeaders();
        // v4.3.47: Use lightweight check-name endpoint instead of full leaderboard
        const r = await fetch(`${API_BASE_URL}/api/teams/check-name?name=${encodeURIComponent(teamName.trim())}`, { headers: h });
        if (cancelled) return;
        if (r.ok) {
          const d = await r.json();
          if (cancelled) return;
          if (!d.available) { setNameAvailable(false); setNameSuggestion(d.reason || 'Name is taken.'); }
          else {
            setNameAvailable(true);
            setNameSuggestion(d.similar ? `Similar to "${d.similar}". Consider something more unique.` : '');
          }
        }
      } catch { /* */ }
      if (!cancelled) setNameChecking(false);
    }, 500);
    return () => { cancelled = true; };
  }, [teamName]);

  // ─── Rename Team ────────────────────────────────────────────────────
  const [renaming, setRenaming] = useState(false);
  const [renameMode, setRenameMode] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [renameError, setRenameError] = useState('');
  const handleRenameTeam = async () => {
    if (!myTeam || !newTeamName.trim() || newTeamName.trim().length < 3) { setRenameError('Name must be at least 3 characters'); return; }
    if (newTeamName.trim() === myTeam.name) { setRenameMode(false); return; }
    setRenaming(true); setRenameError('');
    try {
      const h = await ensureSessionHeaders();
      const r = await fetch(`${API_BASE_URL}/api/teams/rename`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ teamId: myTeam.id, newName: newTeamName.trim() })
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setMyTeam({ ...myTeam, name: d.newName });
        setRenameMode(false);
        loadLeaderboard();
      } else { setRenameError(d.error || 'Rename failed'); }
    } catch { setRenameError('Network error'); }
    finally { setRenaming(false); }
  };

  // ─── Registration ──────────────────────────────────────────────────────
  const handleRegister = async () => {
    if (!teamName.trim() || nameAvailable === false) return;
    setRegistering(true); setRegError('');
    try {
      const h = await ensureSessionHeaders();
      const r = await fetch(`${API_BASE_URL}/api/teams/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ teamName: teamName.trim() })
      });
      const d = await r.json();
      if (r.ok && d.success) { setMyTeam(d.team); setTeamName(''); loadLeaderboard(); }
      else setRegError(d.error || 'Registration failed.');
    } catch (e: any) { setRegError(e.message || 'Network error'); }
    finally { setRegistering(false); }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  const myEntry = myTeam ? leaderboard.find(e => e.team_id === myTeam.id) : undefined;
  const myRank = myTeam ? leaderboard.findIndex(e => e.team_id === myTeam.id) + 1 : 0;
  const myPayout = Number(myEntry?.payout_total) || 0;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #111 50%, #0d0d0d 100%)' }}>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && <button onClick={onBack} className="text-gray-500 hover:text-yellow-400 transition-colors text-sm">← Back</button>}
            <div>
              <h1 className="text-xl md:text-2xl font-bold" style={{ color: '#FFD700' }}>⛏️ Gold Diggers Performance Pool</h1>
              <p className="text-gray-500 text-xs mt-1">{POOL_CONTRIBUTION_PERCENT}% of every share purchase • Top {POOL_WINNER_COUNT} teams win daily • Resets 00:00 UTC</p>
            </div>
          </div>
          <button onClick={() => setShowHowItWorks(!showHowItWorks)} className="text-xs text-gray-400 hover:text-yellow-400 border border-gray-700 hover:border-yellow-700/50 px-3 py-1.5 rounded-lg transition-all">
            {showHowItWorks ? 'Hide Rules' : '📖 How it Works'}
          </button>
        </div>

        {/* ── HOW IT WORKS (collapsible) ──────────────────────────────── */}
        {showHowItWorks && (
          <div className="rounded-xl border border-yellow-800/30 bg-yellow-950/10 p-5 space-y-4 text-sm text-gray-300">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><h4 className="text-yellow-400 font-semibold mb-1">💰 The Pool</h4><p className="text-gray-400">{POOL_CONTRIBUTION_PERCENT}% of every share purchase goes into a daily pool, distributed at midnight UTC to the Top {POOL_WINNER_COUNT} teams.</p></div>
              <div><h4 className="text-yellow-400 font-semibold mb-1">📐 Scoring</h4><p className="text-gray-400">Your team's score = total USD volume of share purchases across your entire referral downline. Every dollar counts equally, regardless of level.</p></div>
              <div><h4 className="text-yellow-400 font-semibold mb-1">🏆 Payouts</h4><p className="text-gray-400">1st: 10% • 2nd: 8% • 3rd: 6% • 4th–10th: 4% each • 11th–20th: 2% each • 21st–40th: 1% each • 41st–50th: 0.8% each. If fewer than 50 teams have volume, unused rank % is split equally among those volume teams as a bonus so 100% of the day's pool is paid. Every winner receives <span className="text-green-400 font-semibold">80% USDT</span> and <span className="text-yellow-400 font-semibold">20% Shares</span>.</p></div>
            </div>
            <div><h4 className="text-yellow-400 font-semibold mb-2">🏅 Qualification Gate</h4>
              <p className="text-gray-500 text-xs mb-3">A single entry gate replaces the previous tier ladder — once you meet it, you compete on equal footing with every other team.</p>
              <div className="grid grid-cols-1 gap-2 text-xs">
                {CATEGORIES.map(c => (
                  <div key={c.name} className="rounded-lg bg-gray-800/40 p-3 border border-gray-700/30 flex items-center gap-4">
                    <div className="text-2xl flex-shrink-0">{c.icon}</div>
                    <div className="flex-1">
                      <div className="font-semibold" style={{color: c.color}}>{c.name}</div>
                      <div className="text-gray-500 mt-0.5">{c.l1} L1 + {c.l2} L2 referrals • each holding ≥{c.shares} share{c.shares>1?'s':''}</div>
                    </div>
                    <div className="flex-shrink-0 w-40">
                      <div className="flex gap-0.5 h-2 rounded-full overflow-hidden">
                        <div className="rounded-l-full" style={{ width: `${c.usdtPct}%`, backgroundColor: '#4ade80' }} />
                        <div className="rounded-r-full" style={{ width: `${c.sharePct}%`, backgroundColor: c.color }} />
                      </div>
                      <div className="flex justify-between mt-1 text-[10px]">
                        <span className="text-green-400">{c.usdtPct}% USDT</span>
                        <span style={{ color: c.color }}>{c.sharePct}% Shares</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── STATE-AWARE HERO SECTION ────────────────────────────────── */}
        {qualLoading ? (
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Checking your qualification status...</p>
          </div>
        ) : hasTeam ? (
          /* ── HAS TEAM: Team card + tier ladder ─────────────────────── */
          <div className="space-y-4">
            {/* Auto-provision welcome banner */}
            {renameMode && myTeam?.name?.startsWith('Team ') && (
              <div className="rounded-xl border border-yellow-600/40 bg-yellow-900/15 p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🎉</span>
                  <div>
                    <div className="text-yellow-400 font-bold text-sm">{t('goldDiggers.welcome', 'Welcome to the Gold Diggers Performance Pool!')}</div>
                    <div className="text-gray-400 text-xs mt-1">Your team has been automatically created based on your qualification. You're now competing in the daily prize pool! Personalize your team name below — your <strong>team ID stays the same</strong>, so all earnings history is preserved.</div>
                  </div>
                </div>
              </div>
            )}
            {/* Team Header */}
            <div className="rounded-xl border border-yellow-700/30 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(120,80,0,0.15), rgba(40,30,0,0.1))' }}>
              <div className="p-5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl" style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.2)' }}>
                      {CATEGORIES[catIdx]?.icon || '⛏️'}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{myTeam.name}</h2>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full uppercase" style={{ backgroundColor: CATEGORIES[0].color + '20', color: CATEGORIES[0].color, border: `1px solid ${CATEGORIES[0].color}40` }}>
                          {CATEGORIES[0].name}
                        </span>
                        <span className="text-gray-600 text-xs">•</span>
                        <span className="text-gray-500 text-xs">L1: <span className="text-yellow-400">{l1Count}</span> • L2: <span className="text-yellow-400">{l2Count}</span></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-center px-4 py-2 rounded-lg bg-gray-800/60 border border-gray-700/30">
                      <div className="text-[10px] text-gray-500 uppercase">{t('goldDiggers.rank', 'Rank')}</div>
                      <div className="text-xl font-bold" style={{ color: myRank <= 3 ? '#FFD700' : myRank > 0 && myRank <= POOL_WINNER_COUNT ? '#4ade80' : '#888' }}>
                        {myRank > 0 ? `#${myRank}` : '—'}
                      </div>
                    </div>
                    <div className="text-center px-4 py-2 rounded-lg bg-gray-800/60 border border-gray-700/30">
                      <div className="text-[10px] text-gray-500 uppercase">Est. Prize</div>
                      <div className="text-xl font-bold text-green-400">
                        {myPayout > 0 ? `$${myPayout.toFixed(2)}` : '$0.00'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── MY EARNINGS (v4.3.50: claim system) ────────────── */}
            <div className="rounded-xl border border-gray-700/30 overflow-hidden" style={{ background: 'rgba(15,25,15,0.4)' }}>
              <div className="px-5 py-3 border-b border-gray-700/30 flex items-center justify-between">
                <h3 className="text-sm font-bold text-green-400">💰 My Pool Earnings</h3>
                <span className="text-xs font-bold text-green-400">${earnings.total.toFixed(2)} total</span>
              </div>
              <div className="p-4">
                {earnings.history.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="text-gray-600 text-sm">{t('goldDiggers.noPayouts', 'No payouts yet')}</div>
                    <div className="text-gray-700 text-xs mt-1">Rankings are calculated daily at 00:05 UTC. If your team places in the Top {POOL_WINNER_COUNT}, your earnings will appear here with a "Claim" button.</div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Unclaimed banner — gated by CLAIMS_ENABLED kill-switch */}
                    {CLAIMS_ENABLED && (earnings.unclaimed_total || 0) > 0 && (
                      <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-3 mb-3 flex items-center justify-between">
                        <div>
                          <div className="text-yellow-400 font-bold text-sm">🎁 ${(earnings.unclaimed_total || 0).toFixed(2)} unclaimed</div>
                          <div className="text-gray-500 text-[10px]">USDT credited to balance + shares purchased with upline commissions</div>
                        </div>
                        <button
                          disabled={claiming}
                          onClick={async () => {
                            const unclaimed = earnings.history.filter(e => !e.claimed).map(e => e.id);
                            if (unclaimed.length === 0) return;
                            if (!confirm(`Claim ${unclaimed.length} pool rewards totaling $${(earnings.unclaimed_total || 0).toFixed(2)}?\n\nUSDT will be credited to your balance.\nShares will be purchased and commissions distributed to your upline.`)) return;
                            setClaiming(true); setClaimResult(null);
                            try {
                              // Claim in batches of 20
                              let totalClaimed = 0, totalErrors = 0;
                              for (let i = 0; i < unclaimed.length; i += 20) {
                                const batch = unclaimed.slice(i, i + 20);
                                const h = await ensureSessionHeaders();
                                const r = await fetch(`${API_BASE_URL}/api/teams/claim-earnings`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json', ...h },
                                  body: JSON.stringify({ winnerIds: batch })
                                });
                                const d = await r.json();
                                totalClaimed += d.totalClaimed || 0;
                                totalErrors += d.totalErrors || 0;
                              }
                              setClaimResult(`✅ Claimed ${totalClaimed} rewards${totalErrors > 0 ? ` (${totalErrors} errors)` : ''}`);
                              loadQualification(); // Refresh data
                            } catch (err: any) { setClaimResult(`❌ ${err.message}`); }
                            finally { setClaiming(false); }
                          }}
                          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 text-black font-bold text-xs rounded-lg transition-colors"
                        >{claiming ? t('goldDiggers.claiming', 'Claiming...') : t('goldDiggers.claimAll', 'Claim All')}</button>
                      </div>
                    )}
                    {claimResult && <div className="text-xs text-center py-1 text-gray-400">{claimResult}</div>}
                    <div className="grid grid-cols-6 text-[10px] text-gray-500 uppercase px-3 pb-1 border-b border-gray-800">
                      <span>{t('goldDiggers.col.date', 'Date')}</span><span className="text-center">{t('goldDiggers.col.rank', 'Rank')}</span><span className="text-right">{t('goldDiggers.col.pool', 'Pool')}</span><span className="text-right">{t('goldDiggers.col.score', 'Score')}</span><span className="text-right">{t('goldDiggers.col.earned', 'Earned')}</span><span className="text-right">{t('goldDiggers.col.status', 'Status')}</span>
                    </div>
                    {earnings.history.map((e, i) => (
                      <div key={i} className={`grid grid-cols-6 text-xs py-1.5 px-3 rounded hover:bg-gray-800/30 items-center ${!e.claimed ? 'bg-yellow-900/5' : ''}`}>
                        <span className="text-gray-400 font-mono">{e.date.slice(5)}</span>
                        <span className="text-center">
                          <span className={`font-bold ${e.rank <= 3 ? 'text-yellow-400' : 'text-gray-300'}`}>
                            {e.rank === 1 ? '🥇' : e.rank === 2 ? '🥈' : e.rank === 3 ? '🥉' : `#${e.rank}`}
                          </span>
                        </span>
                        <span className="text-right text-gray-500 font-mono">${e.pool.toFixed(0)}</span>
                        <span className="text-right text-yellow-400/70 font-mono">{e.score.toFixed(2)}</span>
                        <span className="text-right text-green-400 font-bold font-mono">${e.amount.toFixed(2)}</span>
                        <span className="text-right">
                          {e.claimed ? <span className="text-green-500 text-[10px]">✓ Claimed</span> : (
                            CLAIMS_ENABLED ? (
                              <button
                                disabled={claiming}
                                onClick={async (ev) => {
                                  ev.stopPropagation();
                                  if (!confirm(`Claim $${e.amount.toFixed(2)} for ${e.date}?\n\n$${e.usdt_portion.toFixed(2)} USDT → balance\n$${e.share_portion.toFixed(2)} → shares + upline commissions`)) return;
                                  setClaiming(true); setClaimingId(e.id); setClaimResult(null);
                                  try {
                                    const h = await ensureSessionHeaders();
                                    const r = await fetch(`${API_BASE_URL}/api/teams/claim-earnings`, {
                                      method: 'POST', headers: { 'Content-Type': 'application/json', ...h },
                                      body: JSON.stringify({ winnerIds: [e.id] })
                                    });
                                    const d = await r.json();
                                    if (d.totalClaimed > 0) { setClaimResult(`✅ Claimed $${e.amount.toFixed(2)} for ${e.date}`); loadQualification(); }
                                    else setClaimResult(`❌ ${d.errors?.[0]?.error || 'Claim failed'}`);
                                  } catch (err: any) { setClaimResult(`❌ ${err.message}`); }
                                  finally { setClaiming(false); setClaimingId(null); }
                                }}
                                className="px-2 py-0.5 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 text-black font-bold text-[10px] rounded transition-colors"
                              >{claimingId === e.id ? '⏳...' : t('goldDiggers.claim', 'Claim')}</button>
                            ) : <span className="text-yellow-400 text-[10px]">⏳ {t('goldDiggers.unclaimed', 'Unclaimed')}</span>
                          )}
                        </span>
                      </div>
                    ))}
                    <div className="border-t border-gray-800 mt-2 pt-2 flex justify-between items-center px-3">
                      <span className="text-xs text-gray-500">{earnings.history.length} record{earnings.history.length !== 1 ? 's' : ''}</span>
                      <span className="text-sm font-bold text-green-400">${earnings.total.toFixed(2)} total</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* v4.3.76: Qualification status (single-gate — tier ladder retired). */}
            <div className="rounded-xl border border-gray-700/30 overflow-hidden" style={{ background: 'rgba(20,20,20,0.6)' }}>
              <div className="px-5 py-3 border-b border-gray-700/30 flex items-center justify-between">
                <h3 className="text-sm font-bold text-white">🏅 Qualification Status</h3>
                <span className="text-[10px] text-gray-500 uppercase">Single-gate qualification</span>
              </div>
              <div className="p-4">
                {(() => {
                  const gate = CATEGORIES[0];
                  const tq = tierQualified[gate.shares] || { l1: l1Count, l2: l2Count };
                  const qualL1 = tq.l1;
                  const qualL2 = tq.l2;
                  const l1Met = qualL1 >= gate.l1;
                  const l2Met = qualL2 >= gate.l2;
                  const l1Pct = Math.min(100, (qualL1 / gate.l1) * 100);
                  const l2Pct = Math.min(100, (qualL2 / gate.l2) * 100);
                  const fullyMet = l1Met && l2Met;
                  return (
                    <div className={`rounded-lg p-4 border ${fullyMet ? 'border-green-800/40 bg-green-950/10' : 'border-yellow-800/40 bg-yellow-950/10'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{gate.icon}</span>
                          <div>
                            <div className="font-semibold text-sm" style={{ color: gate.color }}>{gate.name}</div>
                            <div className="text-[11px] text-gray-500">{gate.desc}</div>
                          </div>
                        </div>
                        {fullyMet ? (
                          <span className="text-[10px] bg-green-600 text-black font-bold px-2 py-0.5 rounded uppercase">✓ Qualified</span>
                        ) : (
                          <span className="text-[10px] bg-yellow-600 text-black font-bold px-2 py-0.5 rounded uppercase">In Progress</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                            <span>L1 (≥{gate.shares} share{gate.shares > 1 ? 's' : ''} each)</span>
                            <span className={l1Met ? 'text-green-400' : 'text-gray-400'}>{qualL1}/{gate.l1}</span>
                          </div>
                          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${l1Pct}%`, backgroundColor: l1Met ? '#22c55e' : gate.color }} />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                            <span>L2 (≥{gate.shares} share{gate.shares > 1 ? 's' : ''} each)</span>
                            <span className={l2Met ? 'text-green-400' : 'text-gray-400'}>{qualL2}/{gate.l2}</span>
                          </div>
                          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${l2Pct}%`, backgroundColor: l2Met ? '#22c55e' : gate.color }} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-[10px] text-gray-600">Payout split:</span>
                        <div className="flex-1 flex gap-0.5 h-2 rounded-full overflow-hidden">
                          <div className="rounded-l-full bg-green-500" style={{ width: `${gate.usdtPct}%` }} />
                          <div className="rounded-r-full" style={{ width: `${gate.sharePct}%`, backgroundColor: gate.color }} />
                        </div>
                        <span className="text-[10px] text-gray-500">{gate.usdtPct}% USDT / {gate.sharePct}% Shares</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Rename team */}
            <div className="flex justify-end">
              {renameMode ? (
                <div className="flex items-center gap-2">
                  <input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
                    maxLength={40} placeholder="New team name..."
                    className="px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white focus:border-yellow-500 outline-none w-44" />
                  <button onClick={handleRenameTeam} disabled={renaming}
                    className="text-[11px] bg-yellow-600 hover:bg-yellow-500 text-black font-bold px-3 py-1 rounded transition-colors disabled:opacity-50">
                    {renaming ? '...' : '✓ Save'}
                  </button>
                  <button onClick={() => { setRenameMode(false); setRenameError(''); }}
                    className="text-[11px] text-gray-500 hover:text-white px-2 py-1 transition-colors">✕</button>
                  {renameError && <span className="text-[10px] text-red-400">{renameError}</span>}
                </div>
              ) : (
                <button onClick={() => { setRenameMode(true); setNewTeamName(myTeam?.name || ''); }}
                  className="text-[11px] text-gray-500 hover:text-yellow-400 border border-gray-800 hover:border-gray-600 px-3 py-1 rounded transition-colors">
                  ✏️ Rename Team
                </button>
              )}
            </div>
          </div>
        ) : isQualified ? (
          /* ── QUALIFIED BUT NO TEAM: Show qualification + registration ── */
          <div className="space-y-4">
            {/* Congratulations card */}
            <div className="rounded-xl border border-green-700/30 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(0,80,40,0.15), rgba(0,40,20,0.1))' }}>
              <div className="p-5">
                <div className="flex items-start gap-3 mb-1">
                  <span className="text-3xl">🎉</span>
                  <div>
                    <h2 className="text-lg font-bold text-green-400">You're Qualified to Compete!</h2>
                    <p className="text-gray-400 text-sm mt-1">
                      You've passed the <span className="font-semibold" style={{ color: CATEGORIES[0].color }}>{CATEGORIES[0].icon} {CATEGORIES[0].name}</span> gate
                      with <span className="text-yellow-400 font-semibold">{l1Count}</span> L1 and <span className="text-yellow-400 font-semibold">{l2Count}</span> L2 qualified referrals.
                      Winners receive <span className="text-green-400 font-semibold">{CATEGORIES[0].usdtPct}% USDT</span> / <span className="text-yellow-400 font-semibold">{CATEGORIES[0].sharePct}% Shares</span>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Registration form */}
            <div className="rounded-xl border border-yellow-700/30 overflow-hidden" style={{ background: 'rgba(30,25,10,0.4)' }}>
              <div className="px-5 py-3 border-b border-yellow-700/20">
                <h3 className="text-sm font-bold text-yellow-400">{t('goldDiggers.registerTeam', '✍️ Register Your Team')}</h3>
                <p className="text-gray-500 text-xs mt-0.5">Choose carefully — your team name is <strong className="text-gray-300">permanent</strong> and cannot be changed after creation.</p>
              </div>
              <div className="p-5 max-w-md space-y-3">
                <div className="flex gap-2">
                  <input type="text" value={teamName} onChange={e => setTeamName(e.target.value)} maxLength={100} placeholder="Choose your team name..."
                    className="flex-1 bg-gray-900/80 border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none text-sm" />
                  <button onClick={() => setTeamName(TEAM_NAMES[Math.floor(Math.random() * TEAM_NAMES.length)])} title="Random name"
                    className="bg-gray-700 hover:bg-gray-600 text-yellow-400 px-3 rounded-lg transition-colors text-lg">🎲</button>
                </div>
                {nameChecking && <p className="text-gray-500 text-xs">Checking...</p>}
                {!nameChecking && nameAvailable === true && !nameSuggestion && <p className="text-green-400 text-xs">✓ Available</p>}
                {!nameChecking && nameAvailable === false && <p className="text-red-400 text-xs">✗ {nameSuggestion}</p>}
                {!nameChecking && nameAvailable === true && nameSuggestion && <p className="text-amber-400 text-xs">⚠ {nameSuggestion}</p>}
                {regError && <p className="text-red-400 text-xs bg-red-900/20 border border-red-800/30 rounded-lg p-2">{regError}</p>}
                <button onClick={handleRegister} disabled={registering || !teamName.trim() || nameAvailable === false || nameChecking}
                  className="w-full bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 disabled:opacity-30 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg transition-all text-sm">
                  {registering ? 'Creating...' : '⛏️ Register Team (Permanent — Cannot Be Changed)'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ── NOT QUALIFIED: Progress tracker ──────────────────────── */
          <div className="rounded-xl border border-gray-700/40 overflow-hidden" style={{ background: 'rgba(20,20,20,0.6)' }}>
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-3 mb-5">
                <span className="text-3xl">📊</span>
                <div>
                  <h2 className="text-lg font-bold text-white">{t('goldDiggers.qualificationProgress', 'Your Qualification Progress')}</h2>
                  <p className="text-gray-500 text-sm mt-1">
                    You need <span className="text-yellow-400 font-semibold">{CATEGORIES[0].l1} L1</span> and <span className="text-yellow-400 font-semibold">{CATEGORIES[0].l2} L2</span> referrals — each holding at least <span className="text-yellow-400 font-semibold">{CATEGORIES[0].shares} share{CATEGORIES[0].shares > 1 ? 's' : ''}</span> — to register a team. Build your referral network and ensure each referral holds shares.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700/30">
                  <div className="text-gray-500 text-xs">{t('goldDiggers.yourShares', 'Your Shares')}</div>
                  <div className="text-lg font-bold text-white">{summary.totalShares.toLocaleString()}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700/30">
                  <div className="text-gray-500 text-xs">{t('goldDiggers.l1Referrals', 'L1 Referrals')}</div>
                  <div className="text-lg font-bold text-yellow-400">{l1Count}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700/30">
                  <div className="text-gray-500 text-xs">{t('goldDiggers.l2Referrals', 'L2 Referrals')}</div>
                  <div className="text-lg font-bold text-yellow-400">{l2Count}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700/30">
                  <div className="text-gray-500 text-xs">{t('goldDiggers.status', 'Status')}</div>
                  <div className="text-lg font-bold text-red-400">{t('goldDiggers.notQualified', 'Not Qualified')}</div>
                </div>
              </div>
              <div className="space-y-2">
                {CATEGORIES.map((cat, _i) => {
                  const tq = tierQualified[cat.shares] || { l1: l1Count, l2: l2Count };
                  const qualL1 = tq.l1;
                  const qualL2 = tq.l2;
                  const l1Pct = Math.min(100, (qualL1 / cat.l1) * 100);
                  const l2Pct = Math.min(100, (qualL2 / cat.l2) * 100);
                  const bothMet = qualL1 >= cat.l1 && qualL2 >= cat.l2;
                  const isOpen = expandedTier === cat.name;
                  return (
                    <div key={cat.name} className={`rounded-lg border transition-colors ${bothMet ? 'border-green-800/40 bg-green-950/10' : 'border-gray-700/30 bg-gray-800/20'}`}>
                      <button onClick={() => setExpandedTier(isOpen ? null : cat.name)} className="w-full flex items-center justify-between px-4 py-3 text-left">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{cat.icon}</span>
                          <div>
                            <span className="font-semibold text-sm" style={{ color: cat.color }}>{cat.name}</span>
                            <span className="text-gray-600 text-xs ml-2">{cat.desc}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {bothMet && <span className="text-green-500 text-xs font-semibold">✓ Met</span>}
                          <span className="text-gray-600 text-xs">{isOpen ? '▲' : '▼'}</span>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-4 pt-2 pb-3 mx-4 mb-3 space-y-2 border-t border-gray-700/40">
                          <div>
                            <div className="flex justify-between text-xs mb-1"><span className="text-gray-400">L1 Referrals (with ≥{cat.shares} share{cat.shares>1?'s':''})</span><span className={qualL1 >= cat.l1 ? 'text-green-400' : 'text-gray-400'}>{qualL1}/{cat.l1}</span></div>
                            <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${l1Pct}%`, backgroundColor: qualL1 >= cat.l1 ? '#22c55e' : cat.color }} /></div>
                          </div>
                          <div>
                            <div className="flex justify-between text-xs mb-1"><span className="text-gray-400">L2 Referrals (with ≥{cat.shares} share{cat.shares>1?'s':''})</span><span className={qualL2 >= cat.l2 ? 'text-green-400' : 'text-gray-400'}>{qualL2}/{cat.l2}</span></div>
                            <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${l2Pct}%`, backgroundColor: qualL2 >= cat.l2 ? '#22c55e' : cat.color }} /></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── POOL STATS + LEADERBOARD (always visible) ───────────────── */}
        {(() => {
          const todayData = pastPools.find(p => p.day === competitionDate);
          const todayShares = todayData?.shares_sold || 0;
          const todayRevenue = todayData?.revenue || 0;
          return (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl bg-gray-800/40 p-4 text-center border border-gray-700/30">
                  <div className="text-gray-500 text-xs">{t('goldDiggers.sharesSoldToday', 'Shares Sold Today')}</div>
                  <div className="text-lg font-bold text-white">{todayShares.toFixed(2)}</div>
                </div>
                <div className="rounded-xl bg-gray-800/40 p-4 text-center border border-gray-700/30">
                  <div className="text-gray-500 text-xs">{t('goldDiggers.totalRevenue', 'Total Revenue')}</div>
                  <div className="text-lg font-bold text-white">${todayRevenue.toFixed(2)}</div>
                </div>
                <div className="rounded-xl p-4 text-center border border-yellow-700/20" style={{ background: 'linear-gradient(135deg, rgba(100,70,0,0.15), rgba(40,30,0,0.1))' }}>
                  <div className="text-gray-500 text-xs">{POOL_CONTRIBUTION_PERCENT}% → Pool</div>
                  <div className="text-2xl font-bold" style={{ color: '#FFD700' }}>${poolTotal.toFixed(2)}</div>
                </div>
                <div className="rounded-xl bg-gray-800/40 p-4 text-center border border-gray-700/30">
                  <div className="text-gray-500 text-xs">{t('goldDiggers.activeTeams', 'Active Teams')}</div>
                  <div className="text-lg font-bold text-white">{leaderboard.length}</div>
                </div>
              </div>
              {/* Calculation breakdown */}
              <div className="flex items-center justify-center gap-2 text-[10px] text-gray-600">
                <span>{todayShares.toFixed(2)} shares</span>
                <span>×</span>
                <span>${todayRevenue > 0 ? (todayRevenue / todayShares).toFixed(2) : '0.00'}/share avg</span>
                <span>=</span>
                <span className="text-gray-400">${todayRevenue.toFixed(2)}</span>
                <span>×</span>
                <span className="text-yellow-500 font-semibold">{POOL_CONTRIBUTION_PERCENT}%</span>
                <span>=</span>
                <span className="text-yellow-400 font-semibold">${poolTotal.toFixed(2)} pool</span>
              </div>
            </div>
          );
        })()}

        {/* ── PAST 5 DAYS — What You Could Have Won ──────────────── */}
        {pastPools.length > 1 && (
          <div className="rounded-xl border border-gray-700/30 overflow-hidden" style={{ background: 'rgba(25,15,5,0.4)' }}>
            <div className="px-4 py-3 border-b border-gray-700/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm">📉</span>
                <h3 className="font-bold text-sm text-gray-300">Previous 5 Days — {hasTeam ? 'Pool History' : 'What You Could Have Won'}</h3>
              </div>
              {!hasTeam && <span className="text-[10px] text-red-400/70 font-semibold uppercase">Register a team to start earning</span>}
            </div>
            <div className="p-4">
              <div className="grid grid-cols-5 gap-2">
                {pastPools.filter(p => p.day !== competitionDate).slice(0, 5).map((p) => {
                  const rank1Prize = p.total * payoutDistributionForDate(p.day)[0];
                  return (
                    <div key={p.day} className="rounded-lg bg-gray-800/40 border border-gray-700/20 p-3 text-center">
                      <div className="text-[10px] text-gray-500 font-mono">{p.day.slice(5)}</div>
                      <div className="text-[10px] text-gray-400 mt-1">{p.shares_sold.toFixed(1)} shares</div>
                      <div className="text-[10px] text-gray-500">${p.revenue.toFixed(0)} rev</div>
                      <div className="border-t border-gray-700/30 mt-1 pt-1">
                        <div className="text-[9px] text-gray-600">{POOL_CONTRIBUTION_PERCENT}% →</div>
                        <div className="text-sm font-bold text-yellow-400">${p.total.toFixed(2)}</div>
                      </div>
                      <div className="border-t border-gray-700/30 mt-1 pt-1">
                        <div className={`text-xs font-semibold ${hasTeam ? 'text-gray-400' : 'text-red-400/80 line-through'}`}>
                          ${rank1Prize.toFixed(2)}
                        </div>
                        <div className="text-[9px] text-gray-600">#1 prize</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!hasTeam && pastPools.filter(p => p.day !== competitionDate).length > 0 && (
                <div className="mt-3 text-center">
                  <span className="text-xs text-red-400/60">
                    Total missed (Rank #1): <span className="font-semibold text-red-400">
                      ${pastPools.filter(p => p.day !== competitionDate).slice(0, 5).reduce((sum, p) => sum + p.total * payoutDistributionForDate(p.day)[0], 0).toFixed(2)}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── QUALIFIED BUT NOT EARNING (v4.3.23: paginated) ──────── */}
        {unregistered.length > 0 && (() => {
          const totalPages = Math.ceil(unregistered.length / UNREG_PER_PAGE);
          const pageItems = unregistered.slice(unregPage * UNREG_PER_PAGE, (unregPage + 1) * UNREG_PER_PAGE);
          return (
          <div className="rounded-xl border border-orange-800/20 overflow-hidden" style={{ background: 'rgba(40,20,5,0.3)' }}>
            <div className="px-4 py-3 border-b border-orange-800/15 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm">👻</span>
                <h3 className="font-bold text-sm text-orange-400">Qualified — But Not Earning</h3>
              </div>
              <span className="text-[10px] text-gray-500">{unregistered.length} eligible user{unregistered.length !== 1 ? 's' : ''} without a team</span>
            </div>
            <div className="p-4">
              <p className="text-xs text-gray-500 mb-3">These users qualify to compete but haven't registered a team — they're missing out on daily prize pools.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {pageItems.map((u, i) => {
                  const tierData = CATEGORIES.find(c => c.name.toLowerCase() === u.category) || CATEGORIES[0];
                  return (
                    <div key={i} className="rounded-lg border border-gray-700/30 bg-gray-800/30 p-2.5 text-center relative overflow-hidden">
                      {/* Faded "not earning" overlay */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-red-900/5 pointer-events-none" />
                      <div className="text-lg">{tierData.icon}</div>
                      <div className="text-xs font-medium text-gray-300 mt-1">{(u as any).full_username || u.masked_username}</div>
                      <div className="text-[10px] font-semibold mt-0.5" style={{ color: tierData.color }}>{u.category.toUpperCase()}</div>
                      <div className="text-[9px] text-gray-600 mt-1">{u.l1_count} L1 / {u.l2_count} L2</div>
                      <div className="text-[9px] text-red-400/60 mt-1 font-semibold">❌ Not earning</div>
                    </div>
                  );
                })}
              </div>
              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    onClick={() => setUnregPage(p => Math.max(0, p - 1))}
                    disabled={unregPage === 0}
                    className="px-3 py-1 text-xs rounded border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >← Prev</button>
                  <span className="text-[10px] text-gray-500">
                    Page {unregPage + 1} of {totalPages} ({unregistered.length} total)
                  </span>
                  <button
                    onClick={() => setUnregPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={unregPage >= totalPages - 1}
                    className="px-3 py-1 text-xs rounded border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >Next →</button>
                </div>
              )}
            </div>
          </div>
          );
        })()}

        <div className="rounded-xl border border-gray-700/30 bg-gray-900/30">
          <div className="px-4 py-3 border-b border-gray-700/30 flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-bold text-sm" style={{ color: '#FFD700' }}>🏆 Live Leaderboard</h3>
            <div className="flex items-center gap-3">
              {remainderTotal > 0 && (
                <span className="text-[10px] text-cyan-400">
                  Remainder ${remainderTotal.toFixed(2)} split equally among {volumeTeamCount} volume team{volumeTeamCount === 1 ? '' : 's'}
                </span>
              )}
              <div className="flex items-center gap-3 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: SPONSOR_COLOR }} />Sponsor</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: DOWNLINE_COLOR }} />Downline / leader</span>
                <span className="hidden sm:inline text-gray-600">Matching stripe = same sponsor line</span>
              </div>
              <button onClick={() => loadLeaderboard()} disabled={loading} className="text-xs text-gray-500 hover:text-yellow-400 transition-colors">
                {loading ? '⏳' : '🔄 Refresh'}
              </button>
            </div>
          </div>

          {/* Loading progress bar */}
          {loading && (
            <div className="w-full h-1 bg-gray-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 animate-pulse" style={{ width: '100%', animation: 'shimmer 1.5s ease-in-out infinite' }} />
            </div>
          )}

          {loading && leaderboard.length === 0 ? (
            <div className="p-10 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500 mx-auto mb-2" />
              <p className="text-gray-500 text-xs">Loading team volumes...</p>
              <p className="text-gray-700 text-[10px] mt-1">Reading today's pre-aggregated volume summary</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="p-10 text-center text-gray-600 text-sm">No teams competing yet today. Be the first!</div>
          ) : (
            <DualHScroll deps={[leaderboard.length, expandedTeamId]}>
              <table className="w-full text-sm min-w-max">
                <thead>
                  <tr className="text-gray-500 text-[11px] uppercase border-b border-gray-800">
                    <th className="px-4 py-2.5 text-left w-12">{t('goldDiggers.col.rank', 'Rank')}</th>
                    <th className="px-4 py-2.5 text-left">{t('goldDiggers.col.team', 'Team')}</th>
                    <th className="px-4 py-2.5 text-left">{t('goldDiggers.col.sponsor', 'Sponsor')}</th>
                    <th className="px-4 py-2.5 text-left hidden md:table-cell">{t('goldDiggers.col.leader', 'Leader')}</th>
                    <th
                      className="px-4 py-2.5 text-left hidden md:table-cell cursor-help"
                      title="All qualified teams receive a flat 80% USDT / 20% Shares payout split. Ranking is determined by total USD volume alone."
                    >
                      Status
                    </th>
                    <th
                      className="px-4 py-2.5 text-right cursor-help whitespace-nowrap min-w-[8rem]"
                      title="Total USD volume of today's share purchases across your entire referral downline. Unweighted — every dollar counts equally, regardless of referral level."
                    >
                      Team Volume (USD)
                    </th>
                    <th className="px-4 py-2.5 text-right hidden sm:table-cell">{t('goldDiggers.col.shares', 'Shares')}</th>
                    <th className="px-4 py-2.5 text-right whitespace-nowrap" title="Fixed rank percentage of today's pool">Pool %</th>
                    <th className="px-4 py-2.5 text-right whitespace-nowrap" title="Rank prize before remainder bonus">{t('goldDiggers.col.prize', 'Prize')}</th>
                    <th className="px-4 py-2.5 text-right whitespace-nowrap" title="Unused Top 50 rank % split equally among teams with volume today">Bonus</th>
                    <th className="px-4 py-2.5 text-right whitespace-nowrap">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((e, idx) => {
                    const rank = idx + 1;
                    const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '';
                    const isMe = e.leader_username === currentUsername;
                    const volume = Number(e.total_volume_usd ?? e.weighted_score) || 0;
                    const prize = Number(e.prize_amount) || 0;
                    const bonus = Number(e.bonus_amount) || 0;
                    const totalPayout = Number(e.payout_total) || 0;
                    const poolPct = e.pool_pct;
                    const isExpanded = expandedTeamId === e.team_id;
                    const contribs = e.contributions || [];
                    const sharesByUser = memberShareTotals(contribs);
                    const inspectShareTotal = totalContributionShares(contribs) || Number(e.raw_shares || 0);
                    return (
                      <React.Fragment key={e.team_id}>
                        <tr onClick={() => setExpandedTeamId(isExpanded ? null : e.team_id)}
                          className={`border-b border-gray-800/40 cursor-pointer ${isMe ? 'bg-yellow-900/15' : 'hover:bg-gray-800/20'} transition-colors`}
                          style={{ borderLeft: `3px solid ${lineageStripe(e.sponsor || e.leader_username)}` }}>
                          <td className="px-4 py-2.5 font-bold text-white text-sm">{medal || `#${rank}`}</td>
                          <td className="px-4 py-2.5">
                            <span className={`font-medium text-sm ${isMe ? 'text-yellow-400' : 'text-white'}`}>{e.team_name}</span>
                            {isMe && <span className="text-[10px] text-yellow-600 ml-1.5 font-semibold">(YOU)</span>}
                            {contribs.length > 0 && <span className="text-gray-600 text-[10px] ml-1.5">{isExpanded ? '▲' : '▼'}</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            {e.sponsor ? (
                              <div>
                                <div className="text-xs font-semibold" style={{ color: SPONSOR_COLOR }}>{e.sponsor}</div>
                                <div className="text-[10px] text-gray-500 whitespace-nowrap">
                                  {e.sponsor_pool_rank
                                    ? `#${e.sponsor_pool_rank}${e.sponsor_team_name ? ` · ${e.sponsor_team_name}` : ''}`
                                    : 'Not a pool team leader'}
                                </div>
                              </div>
                            ) : <span className="text-gray-600 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-2.5 text-xs hidden md:table-cell font-medium" style={{ color: DOWNLINE_COLOR }}>{e.leader_username}</td>
                          <td className="px-4 py-2.5 hidden md:table-cell"><span title="Flat 80% USDT / 20% Shares payout for all qualified teams" className="text-[10px] px-1.5 py-0.5 rounded uppercase cursor-help" style={{ backgroundColor: CATEGORIES[0].color + '20', color: CATEGORIES[0].color }}>Qualified</span></td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs whitespace-nowrap min-w-[8rem]" style={{ color: volume > 0 ? '#FFD700' : '#6b7280' }}>${volume.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-gray-400 text-xs hidden sm:table-cell whitespace-nowrap">{Number(e.raw_shares || 0).toFixed(1)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs whitespace-nowrap text-gray-300">{poolPct != null ? `${Number(poolPct).toFixed(2)}%` : <span className="text-gray-700">—</span>}</td>
                          <td className="px-4 py-2.5 text-right">{prize > 0 ? <span className="text-green-400 font-semibold text-xs">${prize.toFixed(2)}</span> : <span className="text-gray-700">—</span>}</td>
                          <td className="px-4 py-2.5 text-right">{bonus > 0 ? <span className="text-cyan-400 font-semibold text-xs">${bonus.toFixed(2)}</span> : <span className="text-gray-700">—</span>}</td>
                          <td className="px-4 py-2.5 text-right">{totalPayout > 0 ? <span className="text-yellow-400 font-bold text-xs">${totalPayout.toFixed(2)}</span> : <span className="text-gray-700">—</span>}</td>
                        </tr>
                        {isExpanded && contribs.length > 0 && (
                          <tr><td colSpan={11} className="p-0">
                            <div className="bg-gray-900/60 border-t border-b border-gray-700/20 px-6 py-3">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="text-[10px] text-gray-500 uppercase font-semibold">Today's network purchases</span>
                                <span className="text-[10px] text-gray-600">— {contribs.length} purchase{contribs.length !== 1 ? 's' : ''} · all times UTC</span>
                                <span className="ml-auto text-[10px] uppercase tracking-wide text-amber-300/90 bg-amber-400/10 border border-amber-400/30 rounded px-2 py-0.5 font-semibold">
                                  Total shares {inspectShareTotal.toFixed(1)}
                                </span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-[11px] min-w-[640px]">
                                  <thead>
                                    <tr className="text-gray-500 uppercase text-[9px]">
                                      <th className="py-1 pr-3 text-left font-semibold">Date (UTC)</th>
                                      <th className="py-1 pr-3 text-left font-semibold">Level</th>
                                      <th className="py-1 pr-3 text-left font-semibold">Buyer</th>
                                      <th className="py-1 pr-3 text-left font-semibold">Sponsor + daily-pool rank</th>
                                      <th className="py-1 pr-3 text-right font-semibold">Shares</th>
                                      <th className="py-1 text-right font-semibold">USD paid</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {contribs.map((c, ci) => (
                                      <tr key={ci} className="border-t border-gray-800/60" style={{ borderLeft: `3px solid ${lineageStripe(c.sponsor || c.user)}` }}>
                                        <td className="py-1.5 pr-3 font-mono text-gray-400 whitespace-nowrap">{formatUtcStamp(c.time)}</td>
                                        <td className="py-1.5 pr-3">
                                          <span className={`font-mono px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                            c.level === 1 ? 'bg-yellow-900/40 text-yellow-400' : c.level <= 3 ? 'bg-blue-900/30 text-blue-400' : 'bg-gray-800 text-gray-500'
                                          }`}>L{c.level}</span>
                                        </td>
                                        <td className="py-1.5 pr-3 font-medium">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <span style={{ color: DOWNLINE_COLOR }}>{c.user}</span>
                                            <span
                                              className="member-share-card shrink-0 font-mono text-[10px] font-semibold text-amber-200 bg-amber-400/10 border border-amber-400/35 rounded px-1.5 py-0.5 whitespace-nowrap"
                                              title={`${Number(sharesByUser.get(c.user) || 0).toFixed(1)} of ${inspectShareTotal.toFixed(1)} team shares today`}
                                            >
                                              {Number(sharesByUser.get(c.user) || 0).toFixed(1)} sh
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-1.5 pr-3">
                                          <div className="font-semibold" style={{ color: SPONSOR_COLOR }}>{c.sponsor || '—'}</div>
                                          <div className="text-[10px] text-gray-500">
                                            {c.sponsor_pool_rank
                                              ? `#${c.sponsor_pool_rank}${c.sponsor_team_name ? ` · ${c.sponsor_team_name}` : ''}${c.sponsor_eligible ? '' : ' · $0 volume'}`
                                              : 'Not a pool team leader'}
                                          </div>
                                        </td>
                                        <td className="py-1.5 pr-3 text-right text-gray-300 whitespace-nowrap">{Number(c.shares || 0).toFixed(1)}</td>
                                        <td className="py-1.5 text-right font-mono font-semibold text-yellow-400 whitespace-nowrap">${Number(c.weighted || 0).toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td></tr>
                        )}
                        {isExpanded && contribs.length === 0 && (
                          <tr><td colSpan={11} className="px-6 py-3 text-center text-gray-600 text-xs bg-gray-900/40">
                            {volume > 0
                              ? 'Volume is recorded for this team, but the purchase breakdown is not available.'
                              : 'No purchases from this team\'s network today yet.'}
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </DualHScroll>
          )}

          {/* Load More button */}
          {lbHasMore && !loading && (
            <div className="px-4 py-3 border-t border-gray-800/30 text-center">
              <button
                onClick={() => loadLeaderboard(true)}
                disabled={lbLoadingMore}
                className="px-5 py-2 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700 transition-colors disabled:opacity-50"
              >
                {lbLoadingMore ? '⏳ Calculating scores...' : `Load More Teams (${leaderboard.length} of ${lbTotalTeams} shown)`}
              </button>
            </div>
          )}
        </div>

        {/* ── POOL HISTORY ─────────────────────────────────────────── */}
        {poolHistory.length > 0 && (
          <div className="rounded-xl border border-gray-700/30 overflow-hidden bg-gray-900/30">
            <div className="px-4 py-3 border-b border-gray-700/30 flex items-center justify-between">
              <h3 className="font-bold text-sm text-amber-400">📜 Competition History</h3>
              <span className="text-[10px] text-gray-500">{poolHistory.length} day{poolHistory.length !== 1 ? 's' : ''} of results</span>
            </div>

            {/* Date navigation */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800/30">
              <button onClick={() => setHistoryPage(p => Math.min(p + 1, poolHistory.length - 1))} disabled={historyPage >= poolHistory.length - 1}
                className="text-xs text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">← Older</button>
              <span className="text-xs text-gray-400 font-mono">{poolHistory[historyPage]?.date || '—'}</span>
              <button onClick={() => setHistoryPage(p => Math.max(p - 1, 0))} disabled={historyPage <= 0}
                className="text-xs text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">Newer →</button>
            </div>

            {(() => {
              const day = poolHistory[historyPage];
              if (!day) return <div className="p-6 text-center text-gray-600 text-xs">No data</div>;
              return (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500">Pool Total</span>
                    <span className="text-sm font-bold text-green-400">${day.pool_total.toFixed(2)}</span>
                  </div>
                  <div className="space-y-1">
                    {day.winners.map((w, i) => {
                      const medal = w.rank === 1 ? '🥇' : w.rank === 2 ? '🥈' : w.rank === 3 ? '🥉' : '';
                      const tierData = CATEGORIES.find(c => c.name.toLowerCase() === w.category) || CATEGORIES[0];
                      return (
                        <div key={i} className="flex items-center justify-between py-1.5 px-3 rounded hover:bg-gray-800/30 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-white w-8">{medal || `#${w.rank}`}</span>
                            <span className="text-white font-medium">{w.team_name}</span>
                            <span className="text-[9px] px-1 py-0.5 rounded bg-gray-800 uppercase" style={{ color: tierData.color }}>{w.category}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-green-400 font-bold font-mono">${w.amount_awarded.toFixed(2)}</span>
                            <span className={`text-[9px] ${w.claimed ? 'text-green-500' : 'text-yellow-400'}`}>{w.claimed ? '✓' : '⏳'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {historyLoading && (
          <div className="text-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-500 mx-auto" /></div>
        )}

      </div>
    </div>
  );
};

export default GoldDiggersDashboard;